/* eslint-disable @atlaskit/design-system/no-nested-styles */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { spacing as spacingOptions } from './constants';
import { ANIMATION_EASE_OUT, LINEAR_TRANSITION_SPEED, TRANSITION_SPEED, varSpacing } from './internal/constants';
import Link from './internal/link';
import Stage from './internal/stage';
const containerStyles = css({
  display: 'grid',
  width: '100%',
  margin: '0 auto',
  padding: "var(--ds-space-0, 0px)",
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  gap: `var(${varSpacing})`,
  listStyleType: 'none',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
  '&&': {
    marginBlockStart: "var(--ds-space-500, 40px)"
  }
});
/**
 * __Progress tracker__
 *
 * A progress tracker displays the steps and progress through a journey.
 *
 * - [Examples](https://atlassian.design/components/progress-tracker/examples)
 * - [Code](https://atlassian.design/components/progress-tracker/code)
 * - [Usage](https://atlassian.design/components/progress-tracker/usage)
 */
const ProgressTracker = ({
  items = [],
  spacing = 'cosy',
  render = {
    // Anchor content is coming from another location
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    link: ({
      item
    }) => jsx(Link, item)
  },
  animated = true,
  testId,
  label = 'Progress'
}) => {
  const prevItems = useRef(items.map(stage => ({
    ...stage,
    percentageComplete: 0
  })));
  const previousStages = items.map(stage => {
    var _prevItems$current;
    const oldStage = (_prevItems$current = prevItems.current) === null || _prevItems$current === void 0 ? void 0 : _prevItems$current.find(st => st.id === stage.id);
    return !!oldStage ? oldStage : stage;
  });
  useEffect(() => {
    prevItems.current = items;
  }, [items]);
  const progressChanges = items.filter((stage, index) => stage.percentageComplete !== previousStages[index].percentageComplete).length;
  const totalStepsForward = items.filter((stage, index) => stage.percentageComplete > previousStages[index].percentageComplete).length;
  const totalStepsBack = items.filter((stage, index) => stage.percentageComplete < previousStages[index].percentageComplete).length;
  let stepsForward = totalStepsForward;
  let stepsBack = totalStepsBack;
  const progressItems = items.map((stage, index) => {
    let transitionSpeed = 0;
    let transitionDelay = 0;
    const transitionEasing = progressChanges > 1 ? 'linear' : ANIMATION_EASE_OUT;
    if (animated) {
      transitionSpeed = progressChanges > 1 ? LINEAR_TRANSITION_SPEED : TRANSITION_SPEED;
      if (stage.percentageComplete < previousStages[index].percentageComplete) {
        /**
         * Load each transition sequentially in reverse.
         */
        transitionDelay = (stepsBack - 1) * transitionSpeed;
        stepsBack -= 1;
      } else if (stage.percentageComplete > previousStages[index].percentageComplete) {
        /**
         * Load each transition sequentially.
         */
        transitionDelay = (totalStepsForward - stepsForward) * transitionSpeed;
        stepsForward -= 1;
      }
    }
    return jsx(Stage, {
      transitionSpeed: transitionSpeed,
      transitionDelay: transitionDelay,
      transitionEasing: transitionEasing,
      key: stage.id,
      item: stage,
      render: render
    });
  });
  const listInlineStyles = {
    gridTemplateColumns: `repeat(${items.length}, 1fr)`,
    [varSpacing]: spacingOptions[spacing],
    maxWidth: 8 * 10 * items.length * 2
  };
  return jsx("ul", {
    "data-testid": testId
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    ,
    style: listInlineStyles,
    css: containerStyles,
    "aria-label": label
  }, progressItems);
};
export default ProgressTracker;